// NOMBASE - Favorites Context
// Manages favorite products for brand users with optimistic updates
import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import { favoritesAPI } from '@/services/api';
import { useAuth } from './AuthContext';

const FavoritesContext = createContext(null);

export const useFavorites = () => {
  const context = useContext(FavoritesContext);
  if (!context) throw new Error('useFavorites must be used within FavoritesProvider');
  return context;
};

export const FavoritesProvider = ({ children }) => {
  const { isAuthenticated, isBrand } = useAuth();
  const [favoriteIds, setFavoriteIds] = useState(new Set());
  const [favorites, setFavorites] = useState([]);
  const [loading, setLoading] = useState(false);

  // Fetch favorites for brand users — deferred off the critical boot
  // path (requestIdleCallback) so it doesn't compete with the page's
  // own fetches; favoriteIds starts empty so consumers are unaffected.
  useEffect(() => {
    if (!isAuthenticated || !isBrand) return;
    let cancelled = false;
    const run = () => {
      if (cancelled) return;
      setLoading(true);
      favoritesAPI.getAll()
        .then(data => {
          if (cancelled) return;
          setFavorites(data);
          setFavoriteIds(new Set(data.map(f => f.product_id)));
        })
        .catch(e => { if (!cancelled) console.error('[FavoritesContext] fetch failed:', e); })
        .finally(() => { if (!cancelled) setLoading(false); });
    };
    const ric = window.requestIdleCallback;
    const handle = ric ? ric(run, { timeout: 2000 }) : setTimeout(run, 600);
    return () => {
      cancelled = true;
      if (ric && window.cancelIdleCallback) window.cancelIdleCallback(handle);
      else clearTimeout(handle);
    };
  }, [isAuthenticated, isBrand]);

  const isFavorite = useCallback((productId) => {
    return favoriteIds.has(productId);
  }, [favoriteIds]);

  const toggleFavorite = useCallback(async (productId) => {
    const wasFavorited = favoriteIds.has(productId);

    // Optimistic update
    setFavoriteIds(prev => {
      const next = new Set(prev);
      if (wasFavorited) next.delete(productId);
      else next.add(productId);
      return next;
    });

    try {
      if (wasFavorited) {
        await favoritesAPI.remove(productId);
        setFavorites(prev => prev.filter(f => f.product_id !== productId));
      } else {
        await favoritesAPI.add(productId);
        // Refetch to get product details
        const updated = await favoritesAPI.getAll();
        setFavorites(updated);
        setFavoriteIds(new Set(updated.map(f => f.product_id)));
      }
    } catch {
      // Rollback on error
      setFavoriteIds(prev => {
        const next = new Set(prev);
        if (wasFavorited) next.add(productId);
        else next.delete(productId);
        return next;
      });
    }
  }, [favoriteIds]);

  const value = {
    favorites,
    favoriteIds,
    isFavorite,
    toggleFavorite,
    favoritesLoading: loading,
    favoritesCount: favoriteIds.size,
  };

  return (
    <FavoritesContext.Provider value={value}>
      {children}
    </FavoritesContext.Provider>
  );
};
